import { useQuery } from '@anchorage/graphql-react';
import type {
  QueryOptions as QueryHookOptions,
  QueryResult,
} from '@anchorage/graphql-react';
import type {
  DocumentNode,
  OperationVariables,
  TypedDocumentNode,
} from '@apollo/client';
import { NetworkStatus } from '@apollo/client';
import { useEffect, useEffectEvent, useMemo, useRef } from 'react';

import {
  DEFAULT_PAGE_SIZE,
  useCursorBasedPagination,
} from '../useCursorBasedPagination';
import type { PageInfo, TablePagination } from '../useCursorBasedPagination';
import { useDeepCompare } from '../useDeepCompare';

export type UsePaginatedQueryOptions<
  TData,
  TVariables extends OperationVariables,
> = {
  /** Non-pagination variables (filters, sort, IDs, etc.). Pagination variables are managed internally. */
  variables?: Omit<TVariables, 'first' | 'after' | 'last' | 'before'>;
  /** Number of items per page. Defaults to 10. */
  pageSize?: number;
  /** Extract PageInfo from query data. */
  getPageInfo: (data: TData) => PageInfo;
  /** Poll interval in ms. */
  pollInterval?: number;
  /** Skip query execution. */
  skip?: boolean;
  /** Apollo fetch policy. */
  fetchPolicy?: QueryHookOptions<TData, TVariables>['fetchPolicy'];
  /** Apollo error policy. */
  errorPolicy?: QueryHookOptions<TData, TVariables>['errorPolicy'];
  /** Apollo link context. */
  context?: QueryHookOptions<TData, TVariables>['context'];
};

export type UsePaginatedQueryResult<
  TData,
  TVariables extends OperationVariables,
> = {
  data: TData | undefined;
  loading: boolean;
  error: Error | undefined;
  tablePagination: TablePagination;
  resetPagination: () => void;
  refetch: QueryResult<TData, TVariables>['refetch'];
};

/**
 * Combines a query hook with cursor-based pagination.
 *
 * Replaces the Apollo `onCompleted`/`onError` callbacks (removed in Apollo 4)
 * with reactive pagination state synced from query data.
 *
 * @example
 * ```tsx
 * const { data, loading, error, tablePagination } = usePaginatedQuery(
 *   WalletAddressesDocument,
 *   {
 *     variables: { vaultID, walletID },
 *     pageSize: 25,
 *     getPageInfo: (data) => data.vault.balancesByAddress.pageInfo,
 *   },
 * );
 * ```
 */
export const usePaginatedQuery = <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  document: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: UsePaginatedQueryOptions<TData, TVariables>,
): UsePaginatedQueryResult<TData, TVariables> => {
  const {
    variables,
    pageSize = DEFAULT_PAGE_SIZE,
    getPageInfo,
    pollInterval,
    skip,
    fetchPolicy,
    errorPolicy,
    context,
  } = options;

  const {
    paginationVariables,
    tablePagination,
    resetPagination,
    updatePagination,
  } = useCursorBasedPagination({ first: pageSize });

  const mergedVariables = useMemo(
    () =>
      ({
        ...variables,
        ...paginationVariables,
      }) as TVariables,
    [variables, paginationVariables],
  );

  const {
    data,
    loading: rawLoading,
    networkStatus,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useQuery<TData, TVariables>(document, {
    variables: mergedVariables,
    skip,
    pollInterval,
    fetchPolicy,
    errorPolicy,
    context,
  });

  // notifyOnNetworkStatusChange defaults to true, so raw `loading` is true on
  // every poll tick. Deriving keeps a genuine manual refetch visible;
  // usePollingQuery derives the same way but exposes it as `fetchState`.
  const loading = rawLoading && networkStatus !== NetworkStatus.poll;

  // Mirror usePollingQuery: stop polling on error so a failing polled query
  // doesn't loop requests during outages, and resume once a later result
  // clears the error — stopPolling halted the timer, so nothing else would
  // restart it. The Effect Events read the latest stopPolling/startPolling
  // without being effect deps; handledErrorRef dedupes so we stop once per
  // distinct error.
  const stoppedDueToErrorRef = useRef(false);
  const stopPollingOnError = useEffectEvent(() => {
    stoppedDueToErrorRef.current = true;
    stopPolling();
  });
  const resumePollingAfterError = useEffectEvent(() => {
    stoppedDueToErrorRef.current = false;
    if (pollInterval) {
      startPolling(pollInterval);
    }
  });
  const handledErrorRef = useRef<Error | undefined>(undefined);
  useEffect(() => {
    if (!pollInterval) {
      return;
    }
    if (error) {
      if (error !== handledErrorRef.current) {
        handledErrorRef.current = error;
        stopPollingOnError();
      }
    } else if (!skip && stoppedDueToErrorRef.current) {
      handledErrorRef.current = undefined;
      resumePollingAfterError();
    }
  }, [error, pollInterval, skip]);

  const queryPageInfo = data ? getPageInfo(data) : undefined;
  const [pageInfoSignal] = useDeepCompare([queryPageInfo]);
  const [variablesSignal] = useDeepCompare({ variables });
  const prevVariablesSignalRef = useRef<number | null>(null);
  useEffect(() => {
    const variablesChanged =
      prevVariablesSignalRef.current !== null &&
      prevVariablesSignalRef.current !== variablesSignal;
    prevVariablesSignalRef.current = variablesSignal;

    if (variablesChanged) {
      resetPagination();
    }

    if (queryPageInfo) {
      updatePagination(queryPageInfo);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [variablesSignal, pageInfoSignal, resetPagination, updatePagination]);

  return useMemo(
    () => ({
      data,
      loading,
      error,
      tablePagination,
      resetPagination,
      refetch,
    }),
    [data, loading, error, tablePagination, resetPagination, refetch],
  );
};
